import Konva from 'konva';
import type { CanvasPoint, HoleGripKind, Shape } from './types';

type DraggedGrip = {
  polylineId: string;
  vertexIndex: number;
  linkedVertexIndices?: number[];
  holeIndex?: number;
  holeGripKind?: HoleGripKind;
  anchor: CanvasPoint;
  shapeKind: Shape['kind'];
};

const areLinkedVertexIndicesEqual = (left?: number[], right?: number[]) =>
  JSON.stringify(left ?? []) === JSON.stringify(right ?? []);

const isSameDraggedGrip = (
  draggedGrip: DraggedGrip | null,
  polylineId: string,
  vertexIndex: number,
  linkedVertexIndices?: number[],
  holeIndex?: number,
  holeGripKind?: HoleGripKind,
) =>
  draggedGrip?.polylineId === polylineId &&
  draggedGrip.vertexIndex === vertexIndex &&
  areLinkedVertexIndicesEqual(draggedGrip.linkedVertexIndices, linkedVertexIndices) &&
  draggedGrip.holeIndex === holeIndex &&
  draggedGrip.holeGripKind === holeGripKind;

export const handleCanvasMouseLeave = (
  backgroundReferenceInput: unknown,
  setHoverSnap: (value: null) => void,
) => {
  if (!backgroundReferenceInput) {
    setHoverSnap(null);
  }
};

export const handleCanvasDoubleClick = (
  event: Konva.KonvaEventObject<MouseEvent>,
  options: {
    isBackgroundReferenceMode: boolean;
    toolMode: string;
    getPointerPosition: (event: Konva.KonvaEventObject<MouseEvent>) => CanvasPoint | null;
    handleSelectionDoubleClick: (point: CanvasPoint) => void;
  },
) => {
  if (options.isBackgroundReferenceMode) return;
  if (options.toolMode !== 'select') return;

  const pos = options.getPointerPosition(event);
  if (!pos) return;

  options.handleSelectionDoubleClick(pos);
};

export const handleCanvasWheel = (
  event: Konva.KonvaEventObject<WheelEvent>,
  options: {
    zoomFactor: number;
    zoomAtScreenPoint: (pointer: CanvasPoint, scaleFactor: number) => void;
  },
) => {
  event.evt.preventDefault();

  const stage = event.target.getStage();
  const pointer = stage?.getPointerPosition();
  if (!pointer) return;

  options.zoomAtScreenPoint(pointer, event.evt.deltaY > 0 ? 1 / options.zoomFactor : options.zoomFactor);
};

export const handleCanvasMouseDown = (
  event: Konva.KonvaEventObject<MouseEvent>,
  options: any,
) => {
  if (options.openToolGroupMenu) {
    options.setOpenToolGroupMenu(null);
  }

  if (event.evt.button === 1) {
    event.evt.preventDefault();
    options.setPanState({
      startX: event.evt.clientX,
      startY: event.evt.clientY,
    });
    return;
  }

  const pos = options.getPointerPosition(event);
  if (!pos) return;

  if (options.isBackgroundCropMode) {
    if (!options.backgroundAsset) return;
    options.setBackgroundCropRect({
      start: pos,
      current: pos,
    });
    return;
  }

  if (options.isBackgroundReferenceMode) {
    if (!options.backgroundAsset || options.backgroundReferenceInput) return;

    if (!options.backgroundReferenceLine) {
      options.setBackgroundReferenceLine({
        start: pos,
        end: null,
        pointer: pos,
      });
      return;
    }

    const end = options.resolveBackgroundReferencePoint(
      pos,
      event.evt.shiftKey,
      options.backgroundReferenceLine,
    );
    options.setBackgroundAsset((current: any) =>
      current
        ? {
            ...current,
            reference: {
              startLocal: options.getBackgroundLocalPoint(current, options.backgroundReferenceLine.start),
              endLocal: options.getBackgroundLocalPoint(current, end),
              realLength: options.getDistance(options.backgroundReferenceLine.start, end),
            },
          }
        : current,
    );
    options.setBackgroundReferenceLine(null);
    options.setBackgroundReferenceInput({
      value: options.formatMeasurementValue(
        options.getDistance(options.backgroundReferenceLine.start, end),
      ),
    });
    options.setSelectedIds([]);
    options.setEditingId(options.backgroundId);
    return;
  }

  if (options.toolMode === 'select') {
    if (options.editingId) {
      return;
    }

    options.setSelectionBox({
      start: pos,
      current: pos,
    });
    return;
  }

  if (options.toolMode === 'move') {
    const resolved = options.resolveMoveCopyPoint(pos, event.evt.shiftKey, options.moveState);
    options.setHoverSnap(resolved.osnap);

    if (!options.moveState) {
      const hitId = options.getEntityHitId(resolved.point);

      if (options.selectedIds.length === 0) {
        if (!hitId) {
          options.setSelectedIds([]);
          options.setEditingId(null);
          options.setMoveState(null);
          return;
        }

        options.setSelectedIds([hitId]);
        options.setEditingId(null);
        return;
      }

      if (options.selectedIds.length === 0) {
        options.setSelectedIds([]);
        options.setEditingId(null);
        options.setMoveState(null);
        return;
      }

      options.beginHistoryAction();
      options.setEditingId(null);
      options.setMoveState(
        options.buildMoveState(
          'move',
          options.selectedIds,
          resolved.point,
          options.polylines.filter((shape: any) => options.selectedIds.includes(shape.id)),
          options.dimensions.filter((dimension: any) => options.selectedIds.includes(dimension.id)),
        ),
      );
      options.focusNumericField('value');
      options.setNumericEdit({
        kind: 'move',
        anchor: resolved.point,
        direction: { x: 1, y: 0 },
        directionValue: options.getDirectionValue({ x: 1, y: 0 }),
        position: resolved.point,
        value: options.formatMeasurementValue(0),
      });
      return;
    }

    options.applyMoveState(options.moveState, resolved.point);
    options.finishHistoryAction();
    options.setMoveState(null);
    options.setNumericEdit(null);
    return;
  }

  if (options.toolMode === 'copy') {
    const resolved = options.resolveMoveCopyPoint(pos, event.evt.shiftKey, options.moveState);
    options.setHoverSnap(resolved.osnap);

    if (!options.moveState) {
      const hitId = options.getEntityHitId(resolved.point);

      if (options.selectedIds.length === 0) {
        if (!hitId) {
          options.setSelectedIds([]);
          options.setEditingId(null);
          options.setMoveState(null);
          return;
        }

        options.setSelectedIds([hitId]);
        options.setEditingId(null);
        return;
      }

      if (options.selectedIds.length === 0) {
        options.setSelectedIds([]);
        options.setEditingId(null);
        options.setMoveState(null);
        return;
      }

      options.beginHistoryAction();
      const moveSelection = options.duplicateEntities(options.selectedIds);

      if (moveSelection.entityIds.length === 0) {
        options.cancelHistoryAction();
        options.setMoveState(null);
        return;
      }

      options.setSelectedIds(moveSelection.entityIds);
      options.setEditingId(null);
      options.setMoveState(
        options.buildMoveState(
          'copy',
          moveSelection.entityIds,
          resolved.point,
          moveSelection.shapes,
          moveSelection.dimensions,
          options.selectedIds,
        ),
      );
      options.focusNumericField('value');
      options.setNumericEdit({
        kind: 'move',
        anchor: resolved.point,
        direction: { x: 1, y: 0 },
        directionValue: options.getDirectionValue({ x: 1, y: 0 }),
        position: resolved.point,
        value: options.formatMeasurementValue(0),
      });
      return;
    }

    if (!options.moveState.vectorLocked) {
      options.applyMoveState(options.moveState, resolved.point);
      options.setMoveState({
        ...options.moveState,
        vectorLocked: true,
      });
      options.setNumericEdit((current: any) => {
        if (current?.kind !== 'move') return current;

        const direction = options.getNormalizedDirection(current.anchor, resolved.point);
        return {
          ...current,
          direction,
          directionValue: options.getDirectionValue(direction),
          position: resolved.point,
          value: options.formatMeasurementValue(
            options.getDistance(current.anchor, resolved.point),
          ),
        };
      });
      options.focusNumericField('value');
      return;
    }

    options.applyMoveState(options.moveState, resolved.point);
    options.finishHistoryAction();
    options.setMoveState(null);
    options.setNumericEdit(null);
    options.setHoverSnap(null);
    return;
  }

  if (options.toolMode === 'offset') {
    if (!options.activeOffset) {
      const offsetTarget = options.getOffsetTargetAtPoint(pos);
      if (!offsetTarget) {
        options.setSelectedIds([]);
        options.setEditingId(null);
        return;
      }

      const shape = options.getShapeById(offsetTarget.entityId);
      if (!shape) return;
      const sourceShape = options.buildOffsetSourceShape(shape, offsetTarget.sourceContour);
      if (!sourceShape) return;

      const defaultDistance = options.getDefaultOffsetDistance();
      options.setSelectedIds([offsetTarget.entityId]);
      options.setEditingId(null);
      options.setActiveOffset({
        entityId: offsetTarget.entityId,
        pointer: pos,
        sourceContour: offsetTarget.sourceContour,
      });
      options.setNumericEdit({
        kind: 'offset',
        entityId: offsetTarget.entityId,
        position: pos,
        value: options.formatMeasurementValue(defaultDistance),
      });
      return;
    }

    const sourceShape = options.getShapeById(options.activeOffset.entityId);
    const parsedDistance =
      options.numericEdit?.kind === 'offset'
        ? options.parseMeasurementValue(options.numericEdit.value)
        : null;
    if (!sourceShape || parsedDistance == null) return;

    options.beginHistoryAction();
    const nextId = options.commitOffset(
      sourceShape,
      pos,
      parsedDistance,
      options.activeOffset.sourceContour,
    );
    if (!nextId) {
      options.cancelHistoryAction();
      return;
    }
    options.finishHistoryAction();

    options.setSelectedIds([nextId]);
    options.setActiveOffset(null);
    options.setNumericEdit(null);
    return;
  }

  if (
    options.toolMode === 'polyline' ||
    options.toolMode === 'singleLifeLine' ||
    options.toolMode === 'buildPolyline' ||
    options.toolMode === 'hatch' ||
    options.isRoofComponentToolMode(options.toolMode)
  ) {
    const current = options.activePolylineRef.current;
    const resolved = options.resolvePolylinePoint(pos, event.evt.shiftKey, current);

    if (options.shouldFinishPolylineOnStartPoint(resolved.point, current)) {
      if (options.isRoofComponentToolMode(options.toolMode)) {
        options.finalizeActiveRoofComponentPolyline(options.toolMode);
      } else if (options.toolMode === 'buildPolyline') {
        options.closeActivePolyline({ disableFill: true });
      } else if (options.toolMode === 'hatch') {
        options.closeActiveHatchPolyline();
      } else if (options.toolMode === 'singleLifeLine') {
        options.finalizeActivePolyline({ closed: true });
      } else {
        options.finalizeActivePolyline({ closed: true });
      }
      return;
    }

    options.handlePolylineMouseDown(resolved.point, resolved.osnap);
    return;
  }

  if (options.toolMode === 'roof' || options.toolMode === 'multiGableRoof') {
    const current = options.activePolylineRef.current;
    const resolved = options.resolvePolylinePoint(pos, event.evt.shiftKey, current);

    if (options.shouldFinishPolylineOnStartPoint(resolved.point, current)) {
      if (options.toolMode === 'multiGableRoof') {
        options.closeActiveMultiGableRoof();
      } else {
        void options.closeActiveRoof();
      }
      return;
    }

    options.handlePolylineMouseDown(resolved.point, resolved.osnap);
    return;
  }

  if (options.toolMode === 'obstaclePolyline') {
    const current = options.activePolylineRef.current;
    const resolved = options.resolvePolylinePoint(pos, event.evt.shiftKey, current);

    if (options.shouldFinishPolylineOnStartPoint(resolved.point, current)) {
      options.closeActiveObstaclePolyline();
      return;
    }

    options.handlePolylineMouseDown(resolved.point, resolved.osnap);
    return;
  }

  if (options.toolMode === 'holePolyline') {
    const host = options.activeHoleHost ? options.getShapeById(options.activeHoleHost.hostId) : null;
    const current = options.activePolylineRef.current;

    if (!options.isHoleHostShape(host) && !current) {
      const hitId = options.getEntityHitId(pos);
      const hitShape = hitId ? options.getShapeById(hitId) : null;
      if (options.isHoleHostShape(hitShape)) {
        options.setActiveHoleHost({ hostId: hitShape.id });
        options.setSelectedIds([hitShape.id]);
        options.setEditingId(null);
      }
      return;
    }

    const resolved = options.resolvePolylinePoint(pos, event.evt.shiftKey, current);

    if (options.shouldFinishPolylineOnStartPoint(resolved.point, current)) {
      options.closeActiveHolePolyline();
      return;
    }

    options.handlePolylineMouseDown(resolved.point, resolved.osnap);
    return;
  }

  if (options.toolMode === 'holeCircle') {
    options.handleHoleCircleMouseDown(pos);
    return;
  }

  if (
    options.toolMode === 'rectangle' ||
    options.toolMode === 'gableRoof' ||
    options.toolMode === 'curvedRoof' ||
    options.toolMode === 'singlePitchedRoof' ||
    options.toolMode === 'multiShedRoof'
  ) {
    const resolved = options.resolveRectanglePoint(pos, event.evt.shiftKey, options.activeRectangle);
    options.setHoverSnap(resolved.osnap);
    options.handleRectangleMouseDown(resolved.point);
    return;
  }

  if (options.toolMode === 'circle') {
    options.handleCircleMouseDown(pos);
    return;
  }

  if (options.toolMode === 'arc') {
    const resolved = options.resolveArcPoint(pos, event.evt.shiftKey, options.activeArc);
    options.setHoverSnap(resolved.osnap);
    options.handleArcMouseDown(resolved.point, resolved.osnap);
    return;
  }

  if (options.toolMode === 'obstacleCircle') {
    options.handleObstacleCircleMouseDown(pos);
    return;
  }

  if (options.toolMode === 'singlePointCircle') {
    options.handleSinglePointCircleMouseDown(pos);
    return;
  }

  if (options.toolMode === 'pointSeries') {
    options.handlePointSeriesMouseDown(pos, event.evt.shiftKey);
    return;
  }

  if (options.toolMode === 'ruler') {
    options.handleRulerMouseDown(pos, event.evt.shiftKey);
    return;
  }

  if (options.toolMode === 'dimension') {
    options.handleDimensionMouseDown(pos);
    return;
  }

  if (options.toolMode === 'dimensionObject') {
    options.handleDimensionObjectMouseDown(pos);
  }
};

export const handleCanvasMouseMove = (
  event: Konva.KonvaEventObject<MouseEvent>,
  options: any,
) => {
  if (options.panState) {
    const deltaX = event.evt.clientX - options.panState.startX;
    const deltaY = event.evt.clientY - options.panState.startY;

    options.setViewport((current: any) => ({
      ...current,
      x: current.x + deltaX,
      y: current.y + deltaY,
    }));

    options.setPanState({
      startX: event.evt.clientX,
      startY: event.evt.clientY,
    });
    return;
  }

  const pos = options.getPointerPosition(event);
  if (!pos) return;

  if (options.isBackgroundCropMode) {
    options.setBackgroundCropRect((current: any) =>
      current
        ? {
            ...current,
            current: pos,
          }
        : current,
    );
    return;
  }

  if (options.isBackgroundReferenceMode) {
    if (options.backgroundReferenceLine && !options.backgroundReferenceInput) {
      options.setBackgroundReferenceLine((current: any) =>
        current
          ? {
              ...current,
              pointer: options.resolveBackgroundReferencePoint(pos, event.evt.shiftKey, current),
            }
          : current,
      );
    }
    return;
  }

  if (options.draggedGrip) {
    return;
  }

  if (options.toolMode === 'select') {
    options.setSelectionBox((current: any) =>
      current
        ? {
            ...current,
            current: pos,
          }
        : current,
    );
    options.setHoverSnap(null);
    return;
  }

  if (options.toolMode === 'move') {
    const resolved = options.resolveMoveCopyPoint(pos, event.evt.shiftKey, options.moveState);
    options.setHoverSnap(resolved.osnap);

    if (!options.moveState) {
      return;
    }

    options.applyMoveState(options.moveState, resolved.point);
    options.setNumericEdit((current: any) => {
      if (current?.kind !== 'move') return current;

      const direction = options.getNormalizedDirection(current.anchor, resolved.point);
      return {
        ...current,
        direction,
        directionValue: options.getDirectionValue(direction),
        position: resolved.point,
        value: options.formatMeasurementValue(
          options.getDistance(current.anchor, resolved.point),
        ),
      };
    });
    return;
  }

  if (options.toolMode === 'copy') {
    const resolved = options.resolveMoveCopyPoint(pos, event.evt.shiftKey, options.moveState);
    options.setHoverSnap(options.moveState?.vectorLocked ? null : resolved.osnap);

    if (!options.moveState) {
      return;
    }

    if (options.moveState.vectorLocked) {
      return;
    }

    options.applyMoveState(options.moveState, resolved.point);
    options.setNumericEdit((current: any) => {
      if (current?.kind !== 'move') return current;

      const direction = options.getNormalizedDirection(current.anchor, resolved.point);
      return {
        ...current,
        direction,
        directionValue: options.getDirectionValue(direction),
        position: resolved.point,
        value: options.formatMeasurementValue(
          options.getDistance(current.anchor, resolved.point),
        ),
      };
    });
    return;
  }

  if (options.toolMode === 'offset') {
    options.setHoverSnap(null);
    options.setActiveOffset((current: any) =>
      current
        ? {
            ...current,
            pointer: pos,
          }
        : current,
    );
    options.setNumericEdit((current: any) =>
      current?.kind === 'offset'
        ? {
            ...current,
            position: pos,
          }
        : current,
    );
    return;
  }

  if (
    options.toolMode === 'rectangle' ||
    options.toolMode === 'gableRoof' ||
    options.toolMode === 'curvedRoof' ||
    options.toolMode === 'singlePitchedRoof' ||
    options.toolMode === 'multiShedRoof'
  ) {
    const resolved = options.resolveRectanglePoint(pos, event.evt.shiftKey, options.activeRectangle);
    options.setHoverSnap(resolved.osnap);
    if (options.numericEdit?.kind === 'multi-shed-count') {
      return;
    }
    options.setActiveRectangle((current: any) => {
      if (!current) return current;
      if (options.numericEdit?.kind === 'rectangle-height' && current.edgeEnd) {
        const baseDx = current.edgeEnd.x - current.start.x;
        const baseDy = current.edgeEnd.y - current.start.y;
        const baseLength = Math.hypot(baseDx, baseDy);

        if (baseLength > 0) {
          const normal = {
            x: -baseDy / baseLength,
            y: baseDx / baseLength,
          };
          const offset =
            (resolved.point.x - current.start.x) * normal.x +
            (resolved.point.y - current.start.y) * normal.y;
          const directionSign = offset >= 0 ? 1 : -1;
          const heightDirection = {
            x: normal.x * directionSign,
            y: normal.y * directionSign,
          };
          const heightLength = Math.abs(offset);
          const nextPointer = {
            x: current.start.x + heightDirection.x * heightLength,
            y: current.start.y + heightDirection.y * heightLength,
          };

          options.setNumericEdit((existing: any) =>
            existing?.kind === 'rectangle-height'
              ? {
                  ...existing,
                  direction: heightDirection,
                  directionValue: options.getDirectionValue(heightDirection),
                  position: nextPointer,
                  value: options.formatMeasurementValue(heightLength),
                }
              : existing,
          );

          return {
            ...current,
            pointer: nextPointer,
          };
        }
      }

      return {
        ...current,
        pointer: resolved.point,
      };
    });
    options.setNumericEdit((current: any) => {
      if (!current) return current;

      if (current.kind === 'rectangle-base') {
        const direction = options.getNormalizedDirection(current.start, resolved.point);
        return {
          ...current,
          direction,
          directionValue: options.getDirectionValue(direction),
          position: resolved.point,
          value: options.formatMeasurementValue(
            options.getDistance(current.start, resolved.point),
          ),
        };
      }

      return current;
    });
    return;
  }

  if (
    options.toolMode === 'circle' ||
    options.toolMode === 'arc' ||
    options.toolMode === 'holeCircle' ||
    options.toolMode === 'obstacleCircle'
  ) {
    if (options.toolMode === 'arc') {
      const resolved = options.resolveArcPoint(pos, event.evt.shiftKey, options.activeArc);
      options.setHoverSnap(resolved.osnap);
      options.setActiveArc((current: any) =>
        current
          ? (() => {
              const direction = options.getNormalizedDirection(current.center, resolved.point);
              options.setNumericEdit((existing: any) =>
                existing?.kind === 'arc'
                  ? {
                      ...existing,
                      center: current.center,
                      start: current.start,
                      direction,
                      directionValue: options.getDirectionValue(direction),
                      position: resolved.point,
                      value: options.formatMeasurementValue(
                        options.getDistance(current.center, resolved.point),
                      ),
                    }
                  : existing,
              );

              return {
                ...current,
                pointer: resolved.point,
              };
            })()
          : current,
      );
      return;
    }

    options.setActiveCircle((current: any) => {
      const resolved = options.resolveSnapPoint(
        pos,
        null,
        null,
        null,
        null,
        true,
        current?.center ?? null,
      );
      options.setHoverSnap(resolved.osnap);
      if (!current) return current;
      if (options.numericEdit?.kind === 'circle') {
        const direction = options.getNormalizedDirection(current.center, resolved.point);
        options.setNumericEdit({
          ...options.numericEdit,
          direction,
          directionValue: options.getDirectionValue(direction),
          position: resolved.point,
          value: options.formatMeasurementValue(
            options.getDistance(current.center, resolved.point),
          ),
        });
      }
      return {
        ...current,
        pointer: resolved.point,
      };
    });
    return;
  }

  if (options.toolMode === 'singlePointCircle') {
    const resolved = options.resolveSnapPoint(pos);
    options.setHoverSnap(resolved.osnap);
    const radius =
      options.numericEdit?.kind === 'single-anchor-point'
        ? options.parseMeasurementValue(options.numericEdit.value) ??
          options.singlePointCircleDefaultRadius
        : options.singlePointCircleDefaultRadius;
    options.setActiveCircle(
      options.buildSinglePointCircleState(resolved.point, Math.max(0, radius)),
    );
    options.setNumericEdit((current: any) =>
      current?.kind === 'single-anchor-point'
        ? {
            ...current,
            center: resolved.point,
            position: resolved.point,
          }
        : current,
    );
    return;
  }

  if (options.toolMode === 'pointSeries') {
    const resolved = options.resolvePointSeriesPoint(
      pos,
      event.evt.shiftKey,
      options.activePointSeries,
    );
    options.setHoverSnap(resolved.osnap);
    options.setNumericEdit((current: any) =>
      current?.kind === 'point-series'
        ? {
            ...current,
            direction: {
              x: resolved.point.x - current.anchor.x,
              y: resolved.point.y - current.anchor.y,
            },
            directionValue: options.getDirectionValue({
              x: resolved.point.x - current.anchor.x,
              y: resolved.point.y - current.anchor.y,
            }),
            position: resolved.point,
            lineDistanceValue: options.formatMeasurementValue(
              options.getDistance(current.anchor, resolved.point),
            ),
          }
        : current,
    );
    return;
  }

  if (options.toolMode === 'ruler') {
    const activePoints =
      options.activeRuler == null
        ? null
        : options.activeRuler.end == null
          ? [
              options.activeRuler.start.x,
              options.activeRuler.start.y,
              options.activeRuler.start.x,
              options.activeRuler.start.y,
            ]
          : null;
    const resolved = options.resolveRulerPoint
      ? options.resolveRulerPoint(pos, event.evt.shiftKey, options.activeRuler)
      : options.resolveSnapPoint(pos, activePoints);
    options.setHoverSnap(resolved.osnap);
    options.setActiveRuler((current: any) =>
      current && current.end == null
        ? {
            ...current,
            pointer: resolved.point,
          }
        : current,
    );
    return;
  }

  if (options.toolMode === 'dimension') {
    const baseActivePoints =
      options.activeDimension == null
        ? null
        : options.activeDimension.end == null
          ? [
              options.activeDimension.start.x,
              options.activeDimension.start.y,
              options.activeDimension.start.x,
              options.activeDimension.start.y,
            ]
          : [
              options.activeDimension.start.x,
              options.activeDimension.start.y,
              options.activeDimension.end.x,
              options.activeDimension.end.y,
              options.activeDimension.end.x,
              options.activeDimension.end.y,
            ];
    const baseResolved = options.resolveSnapPoint(pos, baseActivePoints);
    options.setHoverSnap(baseResolved.osnap);
    options.setActiveDimension((current: any) =>
      current
        ? {
            ...current,
            pointer: baseResolved.point,
          }
        : current,
    );
    return;
  }

  if (
    options.toolMode !== 'polyline' &&
    options.toolMode !== 'singleLifeLine' &&
    options.toolMode !== 'buildPolyline' &&
    options.toolMode !== 'hatch' &&
    !options.isRoofComponentToolMode(options.toolMode) &&
    options.toolMode !== 'roof' &&
    options.toolMode !== 'multiGableRoof' &&
    options.toolMode !== 'obstaclePolyline' &&
    options.toolMode !== 'holePolyline'
  ) {
    options.setHoverSnap(null);
    return;
  }

  options.setActivePolyline((current: any) => {
    const resolved = options.resolvePolylinePoint(pos, event.evt.shiftKey, current);
    options.setHoverSnap(resolved.osnap);

    if (!current) return current;

    const committedPoints = current.slice(0, -2);
    const anchor =
      committedPoints.length >= 2
        ? {
            x: committedPoints[committedPoints.length - 2],
            y: committedPoints[committedPoints.length - 1],
          }
        : null;

    if (anchor) {
      const anchor = {
        x: committedPoints[committedPoints.length - 2],
        y: committedPoints[committedPoints.length - 1],
      };
      const direction = options.getNormalizedDirection(anchor, resolved.point);

      options.setNumericEdit((existing: any) => {
        if (!existing) return existing;

        if (existing.kind === 'polyline') {
          return {
            ...existing,
            anchor,
            direction,
            directionValue: options.getDirectionValue(direction),
            position: resolved.point,
            value: options.formatMeasurementValue(
              options.getDistance(anchor, resolved.point),
            ),
          };
        }

        return existing;
      });
    }

    const nextPoints = current.slice();
    nextPoints[nextPoints.length - 2] = resolved.point.x;
    nextPoints[nextPoints.length - 1] = resolved.point.y;
    return nextPoints;
  });
};

export const handleCanvasMouseUp = (
  event: Konva.KonvaEventObject<MouseEvent>,
  options: any,
) => {
  if (event.evt.button === 1) {
    options.setPanState(null);
    return;
  }

  if (options.isBackgroundCropMode) {
    const currentCropRect = options.backgroundCropRect;
    options.setBackgroundCropRect(null);

    if (currentCropRect) {
      void options.applyBackgroundCrop(currentCropRect.start, currentCropRect.current);
    }

    options.setIsBackgroundCropMode(false);
    return;
  }

  if (options.isBackgroundReferenceMode) return;

  if (options.activeOffset && options.toolMode === 'offset') {
    return;
  }

  if (options.draggedGrip || options.draggedDimensionId || options.toolMode !== 'select') return;

  const pos = options.getPointerPosition(event);
  if (!pos) return;
  const currentSelectionBox = options.selectionBox;
  options.setSelectionBox(null);

  if (!currentSelectionBox) {
    options.handleSelectionClick(pos, event.evt.shiftKey);
    return;
  }

  const dragDistance = options.getDistance(
    currentSelectionBox.start,
    currentSelectionBox.current,
  );
  if (dragDistance < options.selectionDragThreshold / options.viewport.scale) {
    options.handleSelectionClick(pos, event.evt.shiftKey);
    return;
  }

  const selectionBounds = options.getSelectionBounds(
    currentSelectionBox.start,
    currentSelectionBox.current,
  );
  const isCrossingSelection = currentSelectionBox.current.x < currentSelectionBox.start.x;
  const isEntitySelectedByBox = (entityBounds: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  }) =>
    isCrossingSelection
      ? options.boundsIntersect(entityBounds, selectionBounds)
      : entityBounds.minX >= selectionBounds.minX &&
        entityBounds.maxX <= selectionBounds.maxX &&
        entityBounds.minY >= selectionBounds.minY &&
        entityBounds.maxY <= selectionBounds.maxY;

  const shapeIds = options.polylines
    .filter(options.isShapeInteractive)
    .filter((shape: any) => isEntitySelectedByBox(options.getShapeBounds(shape)))
    .map((shape: any) => shape.id);
  const dimensionIds = options.dimensions
    .filter(options.isDimensionInteractive)
    .filter((dimension: any) => isEntitySelectedByBox(options.getDimensionBounds(dimension)))
    .map((dimension: any) => dimension.id);
  const textIds = (options.textEntities ?? [])
    .filter(options.isTextEntityInteractive ?? (() => false))
    .filter((textEntity: any) => isEntitySelectedByBox(options.getTextEntityBounds(textEntity)))
    .map((textEntity: any) => textEntity.id);
  const iconIds = (options.iconEntities ?? [])
    .filter(options.isIconEntityInteractive ?? (() => false))
    .filter((iconEntity: any) => isEntitySelectedByBox(options.getIconEntityBounds(iconEntity)))
    .map((iconEntity: any) => iconEntity.id);
  const nextIds = [...shapeIds, ...dimensionIds, ...textIds, ...iconIds];

  options.setSelectedIds((current: any) =>
    event.evt.shiftKey ? Array.from(new Set([...current, ...nextIds])) : nextIds,
  );

  if (!event.evt.shiftKey) {
    options.setEditingId(null);
  }
};

export const createGripDragHandlers = (options: {
  polylineId: string;
  vertexIndex: number;
  linkedVertexIndices?: number[];
  holeIndex?: number;
  holeGripKind?: HoleGripKind;
  findShape: (id: string) => Shape | undefined;
  getGripAnchorPoint: (
    shape: Shape,
    vertexIndex: number,
    holeIndex?: number,
    holeGripKind?: HoleGripKind,
  ) => CanvasPoint;
  getGripCurrentPoint: (
    shape: Shape,
    vertexIndex: number,
    holeIndex?: number,
    holeGripKind?: HoleGripKind,
  ) => CanvasPoint;
  getGripExcludedSnapSegments: (
    shape: Shape,
    vertexIndex: number,
    holeIndex?: number,
    holeGripKind?: HoleGripKind,
  ) => Array<{ start: CanvasPoint; end: CanvasPoint }>;
  resolveGripPoint: (
    anchor: CanvasPoint,
    point: CanvasPoint,
    shiftKey: boolean,
    excludedPoint?: CanvasPoint | null,
    excludedSegments?: Array<{ start: CanvasPoint; end: CanvasPoint }> | null,
  ) => {
    point: CanvasPoint;
    osnap: unknown;
  };
  isHoleGripUpdateValid: (
    shape: Shape,
    vertexIndex: number,
    nextPoint: CanvasPoint,
    linkedVertexIndices?: number[],
    holeIndex?: number,
    holeGripKind?: HoleGripKind,
  ) => boolean;
  applyShapeGripUpdate: (
    shape: Shape,
    vertexIndex: number,
    nextPoint: CanvasPoint,
    linkedVertexIndices?: number[],
    holeIndex?: number,
    holeGripKind?: HoleGripKind,
  ) => void;
  updateGripNumericEditState: (
    shape: Shape,
    vertexIndex: number,
    anchor: CanvasPoint,
    position: CanvasPoint,
    linkedVertexIndices?: number[],
    holeIndex?: number,
    holeGripKind?: HoleGripKind,
  ) => void;
  beginHistoryAction: () => void;
  cancelHistoryAction: () => void;
  finishHistoryAction: () => void;
  focusNumericField: (target: 'value' | 'direction') => void;
  getCurrentPointerWorldPosition: () => CanvasPoint;
  draggedGrip: DraggedGrip | null;
  setDraggedGrip: (value: DraggedGrip | null) => void;
  setHoverSnap: (value: any) => void;
  setInvalidGripFeedback: (value: { point: CanvasPoint } | null) => void;
}) => ({
  onMouseDown: (event: Konva.KonvaEventObject<MouseEvent>) => {
    event.cancelBubble = true;
  },
  onDragStart: (event: Konva.KonvaEventObject<DragEvent>) => {
    event.cancelBubble = true;
    const shape = options.findShape(options.polylineId);
    if (!shape) return;

    const anchor = options.getGripAnchorPoint(
      shape,
      options.vertexIndex,
      options.holeIndex,
      options.holeGripKind,
    );
    options.beginHistoryAction();
    options.setInvalidGripFeedback(null);
    options.setDraggedGrip({
      polylineId: options.polylineId,
      vertexIndex: options.vertexIndex,
      linkedVertexIndices: options.linkedVertexIndices,
      holeIndex: options.holeIndex,
      holeGripKind: options.holeGripKind,
      anchor,
      shapeKind: shape.kind,
    });
    options.updateGripNumericEditState(
      shape,
      options.vertexIndex,
      anchor,
      anchor,
      options.linkedVertexIndices,
      options.holeIndex,
      options.holeGripKind,
    );
    options.setHoverSnap(null);
  },
  onDragMove: (event: Konva.KonvaEventObject<DragEvent>) => {
    event.cancelBubble = true;
    const shape = options.findShape(options.polylineId);
    if (!shape) return;

    const pointer = options.getCurrentPointerWorldPosition();
    const anchor = isSameDraggedGrip(
      options.draggedGrip,
      options.polylineId,
      options.vertexIndex,
      options.linkedVertexIndices,
      options.holeIndex,
      options.holeGripKind,
    )
      ? options.draggedGrip!.anchor
      : options.getGripAnchorPoint(
          shape,
          options.vertexIndex,
          options.holeIndex,
          options.holeGripKind,
        );
    const currentGripPoint = options.getGripCurrentPoint(
      shape,
      options.vertexIndex,
      options.holeIndex,
      options.holeGripKind,
    );
    const excludedSegments = options.getGripExcludedSnapSegments(
      shape,
      options.vertexIndex,
      options.holeIndex,
      options.holeGripKind,
    );
    const resolved = options.resolveGripPoint(
      anchor,
      pointer,
      event.evt.shiftKey,
      currentGripPoint,
      excludedSegments,
    );
    if (
      !options.isHoleGripUpdateValid(
        shape,
        options.vertexIndex,
        resolved.point,
        options.linkedVertexIndices,
        options.holeIndex,
        options.holeGripKind,
      )
    ) {
      event.target.position(
        options.getGripCurrentPoint(
          shape,
          options.vertexIndex,
          options.holeIndex,
          options.holeGripKind,
        ),
      );
      options.setInvalidGripFeedback({ point: resolved.point });
      return;
    }

    event.target.position(resolved.point);
    options.setInvalidGripFeedback(null);
    options.setHoverSnap(resolved.osnap);
    options.applyShapeGripUpdate(
      shape,
      options.vertexIndex,
      resolved.point,
      options.linkedVertexIndices,
      options.holeIndex,
      options.holeGripKind,
    );
    options.updateGripNumericEditState(
      shape,
      options.vertexIndex,
      anchor,
      resolved.point,
      options.linkedVertexIndices,
      options.holeIndex,
      options.holeGripKind,
    );
  },
  onDragEnd: (event: Konva.KonvaEventObject<DragEvent>) => {
    event.cancelBubble = true;
    const shape = options.findShape(options.polylineId);
    if (!shape) {
      options.cancelHistoryAction();
      options.setDraggedGrip(null);
      options.setHoverSnap(null);
      return;
    }

    const anchor = isSameDraggedGrip(
      options.draggedGrip,
      options.polylineId,
      options.vertexIndex,
      options.linkedVertexIndices,
      options.holeIndex,
      options.holeGripKind,
    )
      ? options.draggedGrip!.anchor
      : options.getGripAnchorPoint(
          shape,
          options.vertexIndex,
          options.holeIndex,
          options.holeGripKind,
        );
    const resolved = {
      point: {
        x: event.target.x(),
        y: event.target.y(),
      },
      osnap: null,
    };
    if (
      !options.isHoleGripUpdateValid(
        shape,
        options.vertexIndex,
        resolved.point,
        options.linkedVertexIndices,
        options.holeIndex,
        options.holeGripKind,
      )
    ) {
      event.target.position(
        options.getGripCurrentPoint(
          shape,
          options.vertexIndex,
          options.holeIndex,
          options.holeGripKind,
        ),
      );
      options.setHoverSnap(null);
      options.setInvalidGripFeedback(null);
    } else {
      event.target.position(resolved.point);
      options.setHoverSnap(null);
      options.setInvalidGripFeedback(null);
      options.applyShapeGripUpdate(
        shape,
        options.vertexIndex,
        resolved.point,
        options.linkedVertexIndices,
        options.holeIndex,
        options.holeGripKind,
      );
      options.updateGripNumericEditState(
        shape,
        options.vertexIndex,
        anchor,
        resolved.point,
        options.linkedVertexIndices,
        options.holeIndex,
        options.holeGripKind,
      );
    }

    options.finishHistoryAction();
    options.setDraggedGrip(null);
    options.setInvalidGripFeedback(null);
    options.focusNumericField('value');
  },
});

export const createGripClickHandler = (_options: {
  polylineId: string;
  vertexIndex: number;
  holeIndex?: number;
  holeGripKind?: HoleGripKind;
  getShapeById: (id: string) => Shape | null;
  getPolylineVertices: (points: number[]) => CanvasPoint[];
}) => (event: Konva.KonvaEventObject<MouseEvent>) => {
  event.cancelBubble = true;
};
